(() => {
  if (window.__INF_SCROLL_RUNNING__) return;
  window.__INF_SCROLL_RUNNING__ = true;

  if (!('IntersectionObserver' in window)) return;

  const SECTION_SEL =
    '.shopify-section--main-collection, .shopify-section--main-search';
  const GRID_SEL =
    'product-list.product-list, blog-posts, .main-search__linklist';
  const NEXT_SEL =
    'a[rel="next"], .pagination__next a, a.pagination__next, .pagination a.next';
  const PAGINATION_SEL = '.pagination, nav.pagination';
  const GROUPS = [
    'product-list.product-list',
    'blog-posts',
    '.main-search__linklist'
  ];

  let grid = null;
  let nextURL = null;
  let observer = null;
  let sentinel = null;
  let controller = null;
  let loading = false;
  let armedFor = null;
  let generation = 0;

  const getSection = () => document.querySelector(SECTION_SEL);

function filterKey(href = location.href) {
  const url = new URL(href, location.href);
  const resultKeys = ['q', 'type', 'sort_by', 'view', 'constraint'];

  for (const key of [...url.searchParams.keys()]) {
    if (
      !resultKeys.includes(key) &&
      !key.startsWith('filter.') &&
      !key.startsWith('options[')
    ) {
      url.searchParams.delete(key);
    }
  }

  url.searchParams.sort();
  return url.pathname + '?' + url.searchParams.toString();
}

  function cleanHref(href) {
    if (!href) return null;

    const url = new URL(href, location.href);

    if (
      url.origin !== location.origin ||
      url.pathname !== location.pathname
    ) return null;

    url.searchParams.delete('section_id');
    return url.pathname + url.search;
  }

  function sectionUrl(href, section) {
    const url = new URL(href, location.href);
    const sectionId = (section.id || '').replace('shopify-section-', '');

    if (sectionId) url.searchParams.set('section_id', sectionId);

    return url.href;
  }

  function showPagination(section) {
    const pagination = section?.querySelector(PAGINATION_SEL);
    if (pagination) pagination.style.display = '';
  }

  function replacePagination(section, nextSection) {
    const current = section.querySelector(PAGINATION_SEL);
    const next = nextSection.querySelector(PAGINATION_SEL);

    if (!next) {
      current?.remove();
    } else if (current) {
      current.replaceWith(next);
    } else {
      section.querySelector('.main-search')?.appendChild(next);
    }

    const pagination = section.querySelector(PAGINATION_SEL);
    if (pagination) pagination.style.display = 'none';
  }

  function appendItems(target, incoming, productsOnly) {
    let added = 0;
    let products = 0;

    for (const item of [...incoming.children]) {
      if (
        productsOnly &&
        !item.matches('product-card, .product-list__promo')
      ) continue;

      item.removeAttribute('reveal-on-scroll');
      item.classList.add('is-revealed');
      target.appendChild(item);
      added++;

      if (item.matches('product-card')) products++;
    }

    return { added, products };
  }

  function appendSearch(section, nextSection) {
    const tabs = section.querySelector('x-tabs');
    const nextTabs = nextSection.querySelector('x-tabs');

    if (!tabs || !nextTabs) {
      throw new Error('Search results unavailable');
    }

    const operations = [];

    for (const selector of GROUPS) {
      const incoming = nextSection.querySelector(selector);
      if (!incoming) continue;

      const target = section.querySelector(selector);

      if (target) {
        operations.push({
          target,
          incoming,
          productsOnly: selector === GROUPS[0]
        });
      } else {
        const panel = incoming.closest('[slot="content"]');
        const panels = [
          ...nextTabs.querySelectorAll(':scope > [slot="content"]')
        ];
        const index = panels.indexOf(panel);
        const button =
          nextTabs.querySelectorAll(':scope > button[slot="title"]')[index];

        const titleSlot =
          tabs.shadowRoot?.querySelector('slot[name="title"]') ||
          tabs.querySelector('template')?.content
            .querySelector('slot[name="title"]');

        if (!panel || !button || !titleSlot) {
          throw new Error('Search tab unavailable');
        }

        operations.push({ panel, button, incoming });
      }
    }

    if (!operations.length) {
      throw new Error('Search results unavailable');
    }

    let added = 0;
    let products = 0;

    for (const operation of operations) {
      if (operation.target) {
        const result = appendItems(
          operation.target,
          operation.incoming,
          operation.productsOnly
        );

        added += result.added;
        products += result.products;
      } else {
        for (const item of operation.incoming.children) {
          item.removeAttribute('reveal-on-scroll');
          item.classList.add('is-revealed');
          added++;

          if (item.matches('product-card')) products++;
        }

        tabs.appendChild(operation.button);
        tabs.appendChild(operation.panel);
      }
    }

    return { added, products };
  }

  function arm() {
    generation++;
    controller?.abort();
    controller = null;
    loading = false;

    observer?.disconnect();
    sentinel?.remove();

    sentinel = null;
    nextURL = null;
    window.__pagesLoaded = 1;
    armedFor = filterKey();

    const section = getSection();
    grid = section?.querySelector(GRID_SEL) || null;

    showPagination(section);

    if (!grid) return;

    const href = cleanHref(
      section.querySelector(NEXT_SEL)?.getAttribute('href')
    );

    if (!href || filterKey(href) !== armedFor) return;

    nextURL = href;

    const pagination = section.querySelector(PAGINATION_SEL);
    if (pagination) pagination.style.display = 'none';

    sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'height:1px;width:100%;';

    const anchor =
      section.querySelector('x-tabs.main-search__tabs') || grid;

    anchor.insertAdjacentElement('afterend', sentinel);

    observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) loadNext();
    }, {
      rootMargin: '800px 0px 800px 0px'
    });

    observer.observe(sentinel);
  }

  async function loadNext() {
    if (!nextURL || loading || !grid) return false;

    loading = true;

    const version = generation;
    const targetGrid = grid;
    const section = getSection();
    const requestKey = armedFor;
    const requestURL = nextURL;
    const abort = new AbortController();

    controller = abort;

    const timeout = setTimeout(() => abort.abort(), 18000);
    let appended = false;

    try {
      const response = await fetch(sectionUrl(requestURL, section), {
        credentials: 'same-origin',
        signal: abort.signal
      });

      if (!response.ok) throw new Error('Page unavailable');

      const doc = new DOMParser().parseFromString(
        await response.text(),
        'text/html'
      );

      if (
        version !== generation ||
        targetGrid !== grid ||
        !targetGrid.isConnected ||
        requestKey !== filterKey()
      ) return false;

      const nextSection = doc.querySelector(SECTION_SEL);

      if (!nextSection) throw new Error('Results unavailable');

      const following = cleanHref(
        nextSection.querySelector(NEXT_SEL)?.getAttribute('href')
      );

      if (following && filterKey(following) !== requestKey) {
        throw new Error('Results changed');
      }

      let result;

      if (section.matches('.shopify-section--main-search')) {
        result = appendSearch(section, nextSection);
      } else {
        const incoming =
          nextSection.querySelector('product-list.product-list');

        if (!incoming) throw new Error('Products unavailable');

        result = appendItems(targetGrid, incoming, true);
      }

      if (!result.added && following) {
        throw new Error('Results unavailable');
      }

      nextURL = following;
      replacePagination(section, nextSection);

      if (result.added) {
        window.__pagesLoaded = (window.__pagesLoaded || 1) + 1;
        appended = true;
      }

      if (result.products) {
        document.dispatchEvent(new CustomEvent('collection:appended', {
          detail: { added: result.products }
        }));
      }

      if (!nextURL) observer?.disconnect();

      return result.added > 0;
    } catch {
      if (version === generation) {
        observer?.disconnect();
        showPagination(section);
        nextURL = null;
      }

      return false;
    } finally {
      clearTimeout(timeout);

      if (version === generation) {
        loading = false;
        controller = null;

        if (appended && nextURL && sentinel) {
          requestAnimationFrame(() => {
            if (version !== generation || !sentinel) return;

            const rect = sentinel.getBoundingClientRect();

            if (
              rect.top < innerHeight + 800 &&
              rect.bottom > -800
            ) loadNext();
          });
        }
      }
    }
  }

  window.__loadNextCollectionPage = loadNext;

  if (!getSection()) return;

  arm();

  const root = document.getElementById('main') || document.body;
  let queued = false;
  let lastHref = location.href;

  new MutationObserver((mutations) => {
    const href = location.href;
    const urlChanged = href !== lastHref;
    lastHref = href;

    if (queued) return;
    if (
      !urlChanged &&
      grid?.isConnected &&
      mutations.every(({ target }) => grid.contains(target))
    ) return;

    queued = true;

    requestAnimationFrame(() => {
      queued = false;

      const nextGrid = getSection()?.querySelector(GRID_SEL) || null;

      if (nextGrid !== grid || filterKey() !== armedFor) arm();
    });
  }).observe(root, {
    childList: true,
    subtree: true
  });
})();